import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { UserPlus, Users, ArrowLeft, Loader2, KeyRound, Check, X, Trash2, Edit } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from '@/components/ui/dialog';
import { authApi, getUser } from '@/lib/api/client';

interface User {
  id: number;
  username: string;
  role: 'ADMIN' | 'USER';
  createdAt: string;
}

export default function UserManagementPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<'ADMIN' | 'USER'>('USER');
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const currentUser = getUser();
  
  // Password change state
  const [editingUserId, setEditingUserId] = useState<number | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [updating, setUpdating] = useState(false);

  // Edit user dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [editUsername, setEditUsername] = useState('');
  const [editRole, setEditRole] = useState<'ADMIN' | 'USER'>('USER');

  const fetchUsers = async () => {
    try {
      const data = await authApi.getUsers();
      setUsers(data);
    } catch (err) {
      console.error('Failed to fetch users', err);
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await authApi.register(username, password, role);
      setSuccess(t('auth.createSuccess'));
      setUsername('');
      setPassword('');
      setRole('USER');
      fetchUsers();
    } catch (err: any) {
      setError(err.response?.data?.message || t('auth.createFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePassword = async (userId: number) => {
    if (!newPassword) return;
    setUpdating(true);
    setError('');
    setSuccess('');

    try {
      await authApi.updatePassword(userId, newPassword);
      setSuccess(t('auth.updateSuccess'));
      setEditingUserId(null);
      setNewPassword('');
    } catch (err: any) {
      setError(err.response?.data?.message || t('auth.updateFailed'));
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteUser = async (userId: number, username: string) => {
    if (!window.confirm(t('auth.confirmDelete', { username }))) return;

    setUpdating(true);
    setError('');
    setSuccess('');

    try {
      await authApi.deleteUser(userId);
      setSuccess(t('auth.deleteSuccess'));
      fetchUsers();
    } catch (err: any) {
      setError(err.response?.data?.message || t('auth.deleteFailed'));
    } finally {
      setUpdating(false);
    }
  };

  const handleOpenEditDialog = (user: User) => {
    setEditingUser(user);
    setEditUsername(user.username);
    setEditRole(user.role);
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setEditingUser(null);
    setEditUsername('');
    setEditRole('USER');
  };

  const handleUpdateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingUser) return;

    setUpdating(true);
    setError('');
    setSuccess('');

    try {
      await authApi.updateUser(editingUser.id, {
        username: editUsername,
        role: editRole,
      });
      setSuccess(t('auth.editSuccess'));
      handleCloseEditDialog();
      fetchUsers();
    } catch (err: any) {
      setError(err.response?.data?.message || t('auth.editFailed'));
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div className="pt-20">
      <div className="container mx-auto px-4 py-8">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4">
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t('stock.backToHome')}
        </Button>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Create User Form */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UserPlus className="h-5 w-5 text-blue-600" />
                {t('auth.createUser')}
              </CardTitle>
              <CardDescription>
                Add a new user to the system.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCreateUser} className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">{t('auth.username')}</label>
                  <Input
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                    placeholder="Enter username"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">{t('auth.password')}</label>
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    placeholder="Enter password"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">{t('auth.role')}</label>
                  <select
                    value={role}
                    onChange={(e) => setRole(e.target.value as 'ADMIN' | 'USER')}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  >
                    <option value="USER">{t('auth.roleUser')}</option>
                    <option value="ADMIN">{t('auth.roleAdmin')}</option>
                  </select>
                </div>
                {error && <p className="text-sm text-red-600">{error}</p>}
                {success && <p className="text-sm text-green-600">{success}</p>}
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {t('auth.createUser')}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* User List */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5 text-blue-600" />
                {t('auth.userList')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {fetching ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  <div className="pb-2 text-xs text-slate-500 grid grid-cols-[1fr_90px_auto] items-center">
                    <span>{t('auth.username')}</span>
                    <span className="text-center">{t('auth.role')}</span>
                    <span className="text-right">{t('auth.actions')}</span>
                  </div>
                  {users.map((user) => (
                    <div key={user.id} className="py-3 grid grid-cols-[1fr_90px_auto] items-center gap-2">
                      <div>
                        <p className="font-medium text-slate-900">{user.username}</p>
                        <p className="text-xs text-slate-500">
                          Created: {new Date(user.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="text-center">
                        <Badge
                          variant="secondary"
                          className={`text-[10px] px-1.5 py-0 h-4 border-none text-white bg-gradient-to-r ${
                            user.role === 'ADMIN'
                              ? 'from-zinc-900 via-amber-200 to-zinc-900 text-amber-200 font-bold border border-amber-500/30'
                              : 'from-blue-500 via-indigo-500 to-cyan-500'
                          } bg-[length:200%_auto] animate-shimmer-once shadow-sm`}
                        >
                          {user.role}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-end gap-2">
                        {editingUserId === user.id ? (
                          <div className="flex items-center gap-2">
                            <Input
                              type="password"
                              size={10}
                              className="h-8 w-32 text-xs"
                              placeholder={t('auth.newPassword')}
                              value={newPassword}
                              onChange={(e) => setNewPassword(e.target.value)}
                              autoFocus
                            />
                            <Button 
                              size="icon" 
                              variant="ghost" 
                              className="h-8 w-8 text-green-600"
                              onClick={() => handleUpdatePassword(user.id)}
                              disabled={updating}
                            >
                              {updating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                            </Button>
                            <Button 
                              size="icon" 
                              variant="ghost" 
                              className="h-8 w-8 text-slate-400"
                              onClick={() => {
                                setEditingUserId(null);
                                setNewPassword('');
                              }}
                              disabled={updating}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        ) : (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-slate-500 hover:text-blue-600 flex items-center gap-1"
                              onClick={() => handleOpenEditDialog(user)}
                            >
                              <Edit className="h-3.5 w-3.5" />
                              <span className="text-xs">{t('auth.editUser')}</span>
                            </Button>

                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-slate-500 hover:text-blue-600 flex items-center gap-1"
                              onClick={() => setEditingUserId(user.id)}
                            >
                              <KeyRound className="h-3.5 w-3.5" />
                              <span className="text-xs">{t('auth.changePassword')}</span>
                            </Button>

                            {currentUser?.sub !== user.id && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-slate-500 hover:text-red-600 flex items-center gap-1"
                                onClick={() => handleDeleteUser(user.id, user.username)}
                                disabled={updating}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                                <span className="text-xs">{t('auth.deleteUser')}</span>
                              </Button>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                  {users.length === 0 && (
                    <p className="text-center py-8 text-slate-500">No users found.</p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Edit User Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent>
            <DialogClose onClick={handleCloseEditDialog} />
            <DialogHeader>
              <DialogTitle>{t('auth.editUserInfo')}</DialogTitle>
              <DialogDescription>
                Update user information and role
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleUpdateUser}>
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">{t('auth.username')}</label>
                  <Input
                    value={editUsername}
                    onChange={(e) => setEditUsername(e.target.value)}
                    required
                    placeholder="Enter username"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">{t('auth.role')}</label>
                  <select
                    value={editRole}
                    onChange={(e) => setEditRole(e.target.value as 'ADMIN' | 'USER')}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  >
                    <option value="USER">{t('auth.roleUser')}</option>
                    <option value="ADMIN">{t('auth.roleAdmin')}</option>
                  </select>
                </div>
                {error && <p className="text-sm text-red-600">{error}</p>}
                {success && <p className="text-sm text-green-600">{success}</p>}
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCloseEditDialog}
                  disabled={updating}
                >
                  {t('auth.cancel')}
                </Button>
                <Button type="submit" disabled={updating}>
                  {updating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {t('auth.save')}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
